export default {
    name: 'controle-especes',
    components: {},
    props: {
        devisesSimultanees: { type: Boolean, default: false }
    },

    data() {
        return {
            is_loading: true,

            billet_principale: { role: null, valeurs: [] },
            billet_secondaire: { role: 'secondaire', valeurs: [] },
            piece_principale: { role: null, valeurs: [] },
            piece_secondaire: { role: 'secondaire', valeurs: [] },

            saisie_billets_principale: {},
            saisie_billets_secondaire: {},
            saisie_pieces_principale: {},
            saisie_pieces_secondaire: {},

            devise_active: 'principale'
        }
    },

    computed: {
        /**
         * Présentation en cartes (une par devise, billets et pièces regroupés),
         * identique quel que soit le pays configuré
         */
        affichage_cartes() {
            return this.devisesSimultanees;
        },

        cartes_devises() {
            if (!this.affichage_cartes) return [];
            const deux_devises = [true, 1, '1', 'true'].includes(this.has_two_currencies);
            if (!deux_devises) {
                // Une seule devise : billets et pièces dans deux cartes côte à côte
                const sigle = String(this.currency_info.principal_currency || '').trim();
                return [
                    { cle: 'b', libelle: 'Billets', valeurs: this.billet_principale.valeurs, saisie: this.saisie_billets_principale },
                    { cle: 'p', libelle: 'Pièces', valeurs: this.piece_principale.valeurs, saisie: this.saisie_pieces_principale }
                ].map(groupe => ({
                    cle: groupe.cle,
                    sigle,
                    titre: groupe.libelle + ' (' + sigle + ')',
                    libelle_total: 'Total ' + groupe.libelle.toLowerCase(),
                    total: this.sumGroup(groupe.valeurs, groupe.saisie),
                    groupes: [groupe]
                }));
            }
            return ['principale', 'secondaire'].map(role => {
                const sigle = String((role === 'principale' ? this.currency_info.principal_currency : this.currency_info.secondary_currency) || '').trim();
                const nom = role === 'principale' ? this.principal_currency : this.secondary_currency;
                return {
                    cle: role,
                    sigle,
                    titre: nom ? nom + ' (' + sigle + ')' : sigle,
                    libelle_total: 'Total ' + sigle,
                    total: role === 'principale' ? this.total_principale : this.total_secondaire,
                    groupes: [
                        { cle: 'b', libelle: 'Billets', valeurs: this['billet_' + role].valeurs, saisie: this['saisie_billets_' + role] },
                        { cle: 'p', libelle: 'Pièces', valeurs: this['piece_' + role].valeurs, saisie: this['saisie_pieces_' + role] }
                    ]
                };
            });
        },

        billets_visible() {
            return (this.devise_active === 'secondaire') ? this.billet_secondaire.valeurs : this.billet_principale.valeurs;
        },

        pieces_visible() {
            return (this.devise_active === 'secondaire') ? this.piece_secondaire.valeurs : this.piece_principale.valeurs;
        },

        saisie_billets_visible() {
            return (this.devise_active === 'secondaire') ? this.saisie_billets_secondaire : this.saisie_billets_principale;
        },

        saisie_pieces_visible() {
            return (this.devise_active === 'secondaire') ? this.saisie_pieces_secondaire : this.saisie_pieces_principale;
        },

        total_principale() {
            return this.sumGroup(this.billet_principale.valeurs, this.saisie_billets_principale)
                + this.sumGroup(this.piece_principale.valeurs, this.saisie_pieces_principale);
        },

        total_secondaire() {
            return this.sumGroup(this.billet_secondaire.valeurs, this.saisie_billets_secondaire)
                + this.sumGroup(this.piece_secondaire.valeurs, this.saisie_pieces_secondaire);
        },

        /**
         * Montant total contrôlé, converti en devise principale
         * (devise secondaire x taux, comme défini dans le paramétrage Devise)
         */
        total_converti() {
            const rate = parseFloat(this.currency_info.rate) || 0;
            return this.total_principale + (this.has_two_currencies ? (this.total_secondaire * rate) : 0);
        },

        /**
         * Équivalent du montant total contrôlé, exprimé en devise secondaire
         * (ex: total en CDF converti en USD), pour affichage informatif
         */
        total_converti_secondaire() {
            const rate = parseFloat(this.currency_info.rate) || 0;

            if (!this.has_two_currencies || rate <= 0) {
                return 0;
            }

            return this.total_converti / rate;
        },

        /**
         * Affiche le récapitulatif des totaux saisis (indépendamment de l'onglet actif)
         * dès qu'une saisie a été faite dans au moins une des deux devises ; chaque
         * total apparaît individuellement dès qu'il est renseigné, sans attendre l'autre
         */
        show_totaux_devises() {
            return this.has_two_currencies && (this.total_principale > 0 || this.total_secondaire > 0);
        }
    },

    watch: {
        total_converti(new_val) {
            this.$emit('totalChange', new_val);
        }
    },

    mounted() {
        this.chargerCoupures();
    },

    methods: {
        chargerCoupures() {
            this.is_loading = true;

            Promise.all([
                this.$store.dispatch('getCoupureBilletage', 'billet'),
                this.$store.dispatch('getCoupureBilletage', 'piece')
            ]).then(([billet_groups, piece_groups]) => {
                this.billet_principale = billet_groups.find(g => g.role !== 'secondaire') || { role: null, valeurs: [] };
                this.billet_secondaire = billet_groups.find(g => g.role === 'secondaire') || { role: 'secondaire', valeurs: [] };
                this.piece_principale = piece_groups.find(g => g.role !== 'secondaire') || { role: null, valeurs: [] };
                this.piece_secondaire = piece_groups.find(g => g.role === 'secondaire') || { role: 'secondaire', valeurs: [] };

                this.saisie_billets_principale = this.initSaisie(this.billet_principale.valeurs);
                this.saisie_billets_secondaire = this.initSaisie(this.billet_secondaire.valeurs);
                this.saisie_pieces_principale = this.initSaisie(this.piece_principale.valeurs);
                this.saisie_pieces_secondaire = this.initSaisie(this.piece_secondaire.valeurs);

                this.is_loading = false;
                this.$emit('totalChange', this.total_converti);
            }).catch(() => {
                this.is_loading = false;
            });
        },

        initSaisie(valeurs) {
            const saisie = {};
            valeurs.forEach(v => { saisie[v] = ''; });
            return saisie;
        },

        sumGroup(valeurs, saisie) {
            let total = 0;
            valeurs.forEach(v => {
                const nb = parseFloat(saisie[v]) || 0;
                total += nb * v;
            });
            return total;
        },

        setDeviseActive(role) {
            this.devise_active = role;
        },

        /**
         * Détail structuré des lignes comptées, pour construction du payload
         * de contrôle envoyé à be_caisse (tokens 'b-{nb}-{valeur}-{role}' / 'p-{nb}-{valeur}-{role}')
         */
        /**
         * Détail des lignes réellement comptées (nombre > 0), formaté pour affichage
         * dans un rapport (récapitulatif du contrôle des espèces)
         */
        getRecapLines() {
            const lines = [];

            const pushLines = (valeurs, saisie, type_label, role) => {
                valeurs.forEach(v => {
                    const nb = parseFloat(saisie[v]) || 0;

                    if (nb > 0) {
                        const sigle = (role === 'secondaire') ? this.currency_info.secondary_currency : this.currency_info.principal_currency;

                        lines.push({
                            type_label,
                            valeur: v,
                            nb,
                            sous_total: nb * v,
                            sigle
                        });
                    }
                });
            };

            pushLines(this.billet_principale.valeurs, this.saisie_billets_principale, 'Billet', this.billet_principale.role);
            pushLines(this.billet_secondaire.valeurs, this.saisie_billets_secondaire, 'Billet', 'secondaire');
            pushLines(this.piece_principale.valeurs, this.saisie_pieces_principale, 'Pièce', this.piece_principale.role);
            pushLines(this.piece_secondaire.valeurs, this.saisie_pieces_secondaire, 'Pièce', 'secondaire');

            return lines;
        },

        getDetailLines() {
            const lines = [];

            const pushLines = (valeurs, saisie, prefix, role) => {
                valeurs.forEach(v => {
                    const nb = parseFloat(saisie[v]) || 0;
                    lines.push({ prefix, valeur: v, nb, role: role || '' });
                });
            };

            pushLines(this.billet_principale.valeurs, this.saisie_billets_principale, 'b', this.billet_principale.role);
            pushLines(this.billet_secondaire.valeurs, this.saisie_billets_secondaire, 'b', 'secondaire');
            pushLines(this.piece_principale.valeurs, this.saisie_pieces_principale, 'p', this.piece_principale.role);
            pushLines(this.piece_secondaire.valeurs, this.saisie_pieces_secondaire, 'p', 'secondaire');

            return lines;
        },

        formatNumbers(num) {
            return new Number(num).toLocaleString("fr-FR");
        }
    }
}
